/**
 * useSessionContinuity — 앱이 살아있는 동안 마지막 활동 시점을 localStorage 에 기록.
 *
 * 다음 cold start (OS 가 백그라운드 프로세스 kill 후 사용자가 다시 진입) 시 AppSplash 가
 * 이 값을 참고해서 "이어서 쓰는 중" 인지 "정식 시작" 인지 판단한다.
 *
 * 기록 시점:
 *   - 마운트 직후 1회 (이번 세션이 시작되었음을 표시)
 *   - visibilitychange → hidden (백그라운드 진입 — OS 가 kill 하기 전 마지막 기회)
 *   - Capacitor appStateChange → !isActive
 *
 * 본 hook 은 RootLayout 에서 한 번만 마운트한다.
 */
import { useEffect } from "react";
import { clearActiveMarker, isNative, markActiveNow, nativeWarn } from "../native";

export function useSessionContinuity(): void {
  useEffect(() => {
    markActiveNow();

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        markActiveNow();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // 사용자가 task switcher 로 앱을 종료할 때 WebView 가 unload 되며 pagehide(persisted=false) 가
    // 발생한다. 이때 lastActiveAt 을 지워서 다음 시작이 fresh cold start 로 보이게 한다.
    // 일반 backgrounding 은 persisted=true 또는 visibilitychange 만 발생 → 마커 유지.
    const handlePageHide = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        clearActiveMarker();
      }
    };
    window.addEventListener("pagehide", handlePageHide);

    let nativeCleanup: (() => void) | null = null;

    if (isNative()) {
      (async () => {
        try {
          const { App } = await import("@capacitor/app");
          const handle = await App.addListener("appStateChange", (state) => {
            if (!state.isActive) {
              markActiveNow();
            }
          });
          nativeCleanup = () => handle.remove();
        } catch (err) {
          nativeWarn("sessionContinuity:register", err);
        }
      })();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      nativeCleanup?.();
    };
  }, []);
}
